/**
 * calcEngine.js — Pay Calculator pure logic
 *
 * All math for the revenue split calculator lives here.
 * No React, no hooks, no side effects — just functions.
 *
 * Spec: docs/PAY_CALCULATOR_V2_PROPOSAL.md
 */

// ─── Utility ────────────────────────────────────────────────────────────

export function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

// ─── §4 Revenue-Scaling Formula ─────────────────────────────────────────

/**
 * Calculate split percentages. Two modes:
 *   autoScale=true  → revenue-scaling formula (Company 40→30%, Orig 10→20%)
 *   autoScale=false → flat values from settings
 *
 * @param {number} revenue
 * @param {object} settings — { splitCompany, splitOriginator, splitProduction, splitPostProduction }
 * @param {boolean} autoScale
 * @returns {{ company: number, orig: number, prod: number, post: number }} percentages (0-100)
 */
export function calculateSplits(revenue, settings, autoScale) {
  if (autoScale) {
    const t = clamp((revenue - 1000) / 19000, 0, 1);
    return {
      company: round2(40 - 10 * t),
      orig:    round2(10 + 10 * t),
      prod:    25,
      post:    25,
    };
  }
  return {
    company: parseFloat(settings?.splitCompany) || 0,
    orig:    parseFloat(settings?.splitOriginator) || 0,
    prod:    parseFloat(settings?.splitProduction) || 0,
    post:    parseFloat(settings?.splitPostProduction) || 0,
  };
}

// ─── §2 Step 2: Empty Department Redistribution ─────────────────────────

/**
 * Redistribute budgets from empty departments.
 *   - If only Prod empty → Prod budget → Post
 *   - If only Post empty → Post budget → Prod
 *   - If both empty → both budgets → Company
 *
 * @param {{ company: number, orig: number, prod: number, post: number }} amounts - dollar amounts
 * @param {boolean} hasProdCrew
 * @param {boolean} hasPostCrew
 * @returns {{ amounts, redistribution }}
 */
export function redistributeEmpty(amounts, hasProdCrew, hasPostCrew) {
  const result = { ...amounts };
  const redistribution = { prodToPost: 0, postToProd: 0, toCompany: 0 };

  if (!hasProdCrew && !hasPostCrew) {
    redistribution.toCompany = result.prod + result.post;
    result.company += result.prod + result.post;
    result.prod = 0;
    result.post = 0;
  } else if (!hasProdCrew) {
    redistribution.prodToPost = result.prod;
    result.post += result.prod;
    result.prod = 0;
  } else if (!hasPostCrew) {
    redistribution.postToProd = result.post;
    result.prod += result.post;
    result.post = 0;
  }

  return { amounts: result, redistribution };
}

// ─── §3.2 Originator — Percentage Split ─────────────────────────────────

/**
 * Calculate originator earnings from absolute percentages.
 *
 * @param {number} pool — originator dollar amount
 * @param {Array<{name, pct, ...}>} originators — each has a pct (0-100)
 * @returns {{ originators: Array, remainder: number, totalPct: number, error: string|null }}
 */
export function calculateOriginatorEarnings(pool, originators) {
  if (!originators || originators.length === 0) {
    return { originators: [], remainder: pool, totalPct: 0, error: null };
  }

  const totalPct = originators.reduce((sum, o) => sum + (parseFloat(o.pct) || 0), 0);

  if (totalPct > 100) {
    return {
      originators: originators.map(o => ({ ...o, earned: 0 })),
      remainder: 0,
      totalPct: round2(totalPct),
      error: `Originator total exceeds 100% (${round2(totalPct)}%)`,
    };
  }

  const result = originators.map(o => {
    const pct = parseFloat(o.pct) || 0;
    return { ...o, earned: round2(pool * pct / 100) };
  });

  const remainder = round2(pool * (100 - totalPct) / 100);

  return { originators: result, remainder, totalPct: round2(totalPct), error: null };
}

// ─── §3.2 Auto-Equal for Originators ────────────────────────────────────

/**
 * Redistribute originator percentages evenly.
 *
 * @param {Array<{pct, ...}>} originators
 * @returns {Array} — same objects with updated pct values summing to 100
 */
export function autoEqualOriginators(originators) {
  const n = originators.length;
  if (n === 0) return [];

  const base = Math.floor(10000 / n) / 100;
  return originators.map((o, i) => ({
    ...o,
    pct: i === 0 ? round2(100 - base * (n - 1)) : base,
  }));
}

// ─── §5 Department Earnings — Budget-Owner Model ────────────────────────

/**
 * Calculate earnings for a budget-owner department (Production or Post).
 *
 * Lead gets remainder after hourly costs.
 * Hourly crew is ALWAYS paid in full — overrun absorbed by Company.
 *
 * @param {number} budget — department dollar amount
 * @param {object|null} lead — { name, rosterId, crewId } or null (Company is lead)
 * @param {Array<{name, hourlyRate, estHours, rosterId, crewId}>} hourlyMembers
 * @param {Array<{person, hours, jobId}>} timeEntries — actual logged time for this job
 * @returns {{ lead, hourly: Array, budget, hourlyTotal, overrun, leadEarned }}
 */
export function calculateDeptEarnings(budget, lead, hourlyMembers, timeEntries) {
  const hourly = (hourlyMembers || []).map(m => {
    const name = m.name || '';
    const rate = parseFloat(m.hourlyRate) || 0;
    const estHours = parseFloat(m.estHours) || 0;

    // Sum actual hours from time entries for this person
    const actualHours = (timeEntries || [])
      .filter(e => (e.person || '').toLowerCase() === name.toLowerCase())
      .reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);

    const hours = actualHours > 0 ? actualHours : estHours;
    const earned = round2(hours * rate);
    const isEstimated = actualHours === 0 && estHours > 0;

    return {
      ...m,
      hourlyRate: rate,
      hours,
      estHours,
      isEstimated,
      earned,
    };
  });

  const hourlyTotal = hourly.reduce((sum, m) => sum + m.earned, 0);
  const overrun = Math.max(0, round2(hourlyTotal - budget));
  const leadEarned = Math.max(0, round2(budget - hourlyTotal));

  const leadResult = lead ? { ...lead, earned: leadEarned } : null;

  return {
    lead: leadResult,
    hourly,
    budget,
    hourlyTotal: round2(hourlyTotal),
    overrun,
    leadEarned,
  };
}

// ─── §6 Expense Deductions — Reduce Lead Earnings ──────────────────────

/**
 * Apply job expense deductions by department.
 *
 * Each department's expenses are deducted from that department only:
 *   - expensesByDept.orig → deducted from originators proportionally by pct
 *   - expensesByDept.prod → deducted from prod lead earnings
 *   - expensesByDept.post → deducted from post lead earnings
 *   - Company-dept expenses are excluded before calling this function
 *   - Hourly crew is NEVER affected
 *   - Company share is NEVER affected
 *   - Lead earnings clamped at 0 (no negative earnings)
 *
 * @param {{ originators, remainder, totalPct, error }} origResult
 * @param {object} prodResult — from calculateDeptEarnings
 * @param {object} postResult — from calculateDeptEarnings
 * @param {{ orig?: number, prod?: number, post?: number }} expensesByDept — per-department totals
 * @returns {{ origResult, prodResult, postResult, expenseMeta }}
 */
export function applyExpenseDeductions(origResult, prodResult, postResult, expensesByDept) {
  // Support legacy single-number call: treat as even split across occupied roles
  const byDept = typeof expensesByDept === 'number'
    ? _legacyEvenSplit(expensesByDept, origResult, prodResult, postResult)
    : { orig: expensesByDept?.orig || 0, prod: expensesByDept?.prod || 0, post: expensesByDept?.post || 0 };

  const totalExpenses = round2(byDept.orig + byDept.prod + byDept.post);

  if (totalExpenses <= 0) {
    return {
      origResult, prodResult, postResult,
      expenseMeta: { totalExpenses: 0, byDept: { orig: 0, prod: 0, post: 0 }, absorbed: {}, unabsorbed: 0 },
    };
  }

  const absorbed = {};
  let totalAbsorbed = 0;

  // ── Adjust originators ────────────────────────────────────────────────
  let adjOrigResult = origResult;
  if (byDept.orig > 0 && origResult.originators && origResult.originators.length > 0) {
    const totalPct = origResult.originators.reduce((s, o) => s + (parseFloat(o.pct) || 0), 0);
    let origAbsorbed = 0;
    const adjOriginators = origResult.originators.map(o => {
      const pct = parseFloat(o.pct) || 0;
      const share = totalPct > 0 ? round2(byDept.orig * pct / totalPct) : round2(byDept.orig / origResult.originators.length);
      const deduction = Math.min(share, o.earned);
      origAbsorbed += deduction;
      return { ...o, earned: round2(o.earned - deduction), expenseDeduction: round2(deduction) };
    });
    absorbed.orig = round2(origAbsorbed);
    totalAbsorbed += origAbsorbed;
    adjOrigResult = { ...origResult, originators: adjOriginators };
  } else if (byDept.orig > 0) {
    // Originator expenses but no originators → unabsorbed
    absorbed.orig = 0;
  }

  // ── Adjust prod lead ──────────────────────────────────────────────────
  let adjProdResult = prodResult;
  if (byDept.prod > 0 && prodResult.lead) {
    const deduction = Math.min(byDept.prod, prodResult.leadEarned);
    absorbed.prod = round2(deduction);
    totalAbsorbed += deduction;
    const adjLeadEarned = round2(prodResult.leadEarned - deduction);
    adjProdResult = {
      ...prodResult,
      leadEarned: adjLeadEarned,
      lead: { ...prodResult.lead, earned: adjLeadEarned, expenseDeduction: round2(deduction) },
      expenseDeduction: round2(deduction),
    };
  } else if (byDept.prod > 0) {
    absorbed.prod = 0;
  }

  // ── Adjust post lead ──────────────────────────────────────────────────
  let adjPostResult = postResult;
  if (byDept.post > 0 && postResult.lead) {
    const deduction = Math.min(byDept.post, postResult.leadEarned);
    absorbed.post = round2(deduction);
    totalAbsorbed += deduction;
    const adjLeadEarned = round2(postResult.leadEarned - deduction);
    adjPostResult = {
      ...postResult,
      leadEarned: adjLeadEarned,
      lead: { ...postResult.lead, earned: adjLeadEarned, expenseDeduction: round2(deduction) },
      expenseDeduction: round2(deduction),
    };
  } else if (byDept.post > 0) {
    absorbed.post = 0;
  }

  return {
    origResult: adjOrigResult,
    prodResult: adjProdResult,
    postResult: adjPostResult,
    expenseMeta: {
      totalExpenses,
      byDept,
      absorbed,
      unabsorbed: round2(totalExpenses - totalAbsorbed),
    },
  };
}

/** Legacy helper: convert a single number to even-split byDept for backward compat */
function _legacyEvenSplit(total, origResult, prodResult, postResult) {
  if (!total || total <= 0) return { orig: 0, prod: 0, post: 0 };
  const roles = [];
  if (origResult.originators && origResult.originators.length > 0) roles.push('orig');
  if (prodResult.lead) roles.push('prod');
  if (postResult.lead) roles.push('post');
  if (roles.length === 0) return { orig: 0, prod: 0, post: 0 };
  const perRole = round2(total / roles.length);
  return {
    orig: roles.includes('orig') ? perRole : 0,
    prod: roles.includes('prod') ? perRole : 0,
    post: roles.includes('post') ? perRole : 0,
  };
}

// ─── §8 Build Snapshot ──────────────────────────────────────────────────

/**
 * Assemble the canonical calcSnapshot from computed results.
 *
 * @param {object} params
 * @param {number} params.revenue
 * @param {boolean} params.autoScale
 * @param {{ company, orig, prod, post }} params.splits — percentages
 * @param {{ company, orig, prod, post }} params.amounts — dollar amounts (after redistribution)
 * @param {{ prodToPost, postToProd, toCompany }} params.redistribution
 * @param {{ originators, remainder, totalPct, error }} params.origResult
 * @param {object} params.prodResult — from calculateDeptEarnings
 * @param {object} params.postResult — from calculateDeptEarnings
 * @param {object} [params.expenseMeta] — from applyExpenseDeductions
 * @param {object} [params.existingSnapshot] — previous snapshot to merge (preserves quote keys)
 * @returns {object} canonical snapshot
 */
export function buildSnapshot({
  revenue, autoScale, splits, amounts, redistribution,
  origResult, prodResult, postResult, expenseMeta, existingSnapshot,
}) {
  // Calculate total overrun absorbed by company
  const totalOverrun = (prodResult?.overrun || 0) + (postResult?.overrun || 0);

  // Company share: base + originator remainder + lead-as-company earnings + dept overruns
  const companyFromPostLead = postResult?.lead === null ? (postResult?.leadEarned || 0) : 0;
  const companyFromProdLead = prodResult?.lead === null ? (prodResult?.leadEarned || 0) : 0;

  const companyTotal = round2(
    (amounts?.company || 0)
    + (origResult?.remainder || 0)
    + companyFromProdLead
    + companyFromPostLead
    - totalOverrun
  );

  const snapshot = {
    // Preserve existing quote keys (deliverableLines, quoteNumber, mode, sqft)
    ...(existingSnapshot || {}),

    // Configuration
    autoScale: !!autoScale,
    splits: { ...splits },

    // Dollar amounts
    amounts: {
      company: companyTotal,
      orig: amounts?.orig || 0,
      prod: amounts?.prod || 0,
      post: amounts?.post || 0,
    },
    totalRevenue: revenue,

    // Redistribution tracking
    redistribution: { ...redistribution },

    // Originator pool
    originators: origResult?.originators || [],
    originatorRemainder: origResult?.remainder || 0,

    // Production — budget-owner
    prod: {
      lead: prodResult?.lead || null,
      hourly: prodResult?.hourly || [],
      budget: prodResult?.budget || 0,
      hourlyTotal: prodResult?.hourlyTotal || 0,
      overrun: prodResult?.overrun || 0,
      leadEarned: prodResult?.leadEarned || 0,
    },

    // Post Production — budget-owner
    post: {
      lead: postResult?.lead || null,
      hourly: postResult?.hourly || [],
      budget: postResult?.budget || 0,
      hourlyTotal: postResult?.hourlyTotal || 0,
      overrun: postResult?.overrun || 0,
      leadEarned: postResult?.leadEarned || 0,
    },

    savedAt: new Date().toISOString(),

    // Expense deductions
    expenseMeta: expenseMeta || null,
  };

  // A legacy-mode save supersedes any per-item allocation — stale itemAllocs
  // must not shadow the fresh prod/post data (itemSplits stay: line-keyed,
  // reusable if the job returns to items mode)
  delete snapshot.payMode;
  delete snapshot.itemAllocs;
  // Also clear a stale staycation plan (its derived dollars would otherwise
  // contradict the fresh amounts and mislead any reader of snap.staycation)
  delete snapshot.staycation;

  return snapshot;
}

// ═══════════════════════════════════════════════════════════════════════
// ITEMS MODE (payMode: 'items') — per-line-item pay allocation
//
// Waterfall per item piece:
//   effAmount − company% − orig%  =  item budget
//     ├─ shoot share (prod ratio) → shoot lead − shoot hourly
//     └─ edit share  (post ratio) → edit lead  − edit hourly
//
// A piece = a deliverable line, or one sub-piece of it when the line has
// an itemSplits entry. Pieces are keyed by stable line sid (+ ':' + subId).
// Company is the RESIDUAL (revenue − origPool − Σ budgets) so every penny
// conserves exactly regardless of per-piece rounding.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Deterministic stable id for a deliverable line that lacks one.
 * Both the quote builder (persistent minting) and the calculator (display
 * backfill for legacy data) derive the SAME id for the same line, so
 * allocations keyed before a requote still match after it.
 *
 * @param {object} line — deliverable line
 * @param {number} idx — index within the NON-ADDON line sequence
 */
export function lineSid(line, idx) {
  const str = `${line?.productId || ''}|${line?.desc || ''}|${idx}`;
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0; // djb2
  }
  return 'li_' + h.toString(16).padStart(8, '0');
}

/** Return lines with a sid on every entry (existing sids kept). */
export function ensureLineSids(lines) {
  return (lines || []).map((l, i) => (l && l.sid ? l : { ...l, sid: lineSid(l, i) }));
}

/**
 * Deterministic stable id for an RE add-on line saved before add-ons carried
 * one. Same hash as lineSid, own prefix, so it can never collide with a
 * deliverable line's sid.
 *
 * @param {object} line — calc_snapshot.addOnLines entry
 * @param {number} idx — index within addOnLines
 */
export function addonSid(line, idx) {
  return 'ao_' + lineSid(line, idx).slice(3);
}

/**
 * The lines a per-item pay split allocates over, in quote order: the
 * deliverable lines, plus every RE add-on as an item of its own placed after
 * the line it followed on the quote (`afterSid`; null = before every line).
 * Add-ons saved before they carried a position, or whose line is gone, go last.
 */
export function itemLines(snapshot) {
  const main = ensureLineSids(snapshot?.deliverableLines || []);
  const addons = (snapshot?.addOnLines || []).map((l, i) => (l && l.sid ? l : { ...l, sid: addonSid(l, i) }));
  if (addons.length === 0) return main;

  const mainSids = new Set(main.map(l => l.sid));
  const lead = [];
  const tail = [];
  const after = new Map(); // main sid → add-ons that follow it
  addons.forEach(a => {
    if (a.afterSid === null) lead.push(a);
    else if (a.afterSid && mainSids.has(a.afterSid)) {
      if (!after.has(a.afterSid)) after.set(a.afterSid, []);
      after.get(a.afterSid).push(a);
    } else tail.push(a);
  });

  const out = [...lead];
  main.forEach(l => { out.push(l, ...(after.get(l.sid) || [])); });
  return [...out, ...tail];
}

/**
 * Flatten deliverable lines (+ optional itemSplits) into allocation pieces.
 *
 * itemSplits: { [sid]: [{ id, desc, amount }] } — calculator-owned sub-division
 * of one line. A split only applies when its amounts sum to the line amount
 * (±$0.01); otherwise the line stays unsplit and is reported in mismatches.
 *
 * @returns {{ pieces: Array<{key, sid, subId, desc, lineDesc, amount}>, mismatches: Array }}
 */
export function buildItemPieces(deliverableLines, itemSplits) {
  const lines = ensureLineSids(deliverableLines);
  const pieces = [];
  const mismatches = [];

  lines.forEach(line => {
    // Prefer the stored amount over qty × rate: on VirTour lines the amount is
    // the payout share of retail, so recomputing from rate would allocate
    // revenue at retail proportions. Everywhere else the two are written
    // identically, so this is a no-op for RE/custom snapshots.
    const storedAmount = parseFloat(line.amount);
    const lineAmount = round2(
      Number.isFinite(storedAmount)
        ? storedAmount
        : line.qty != null && line.rate != null
          ? (parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0)
          : 0
    );
    const split = itemSplits?.[line.sid];

    if (Array.isArray(split) && split.length > 0) {
      const sum = round2(split.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0));
      if (Math.abs(sum - lineAmount) <= 0.01) {
        split.forEach(p => {
          pieces.push({
            key: `${line.sid}:${p.id}`,
            sid: line.sid,
            subId: p.id,
            desc: `${line.desc} — ${p.desc}`,
            lineDesc: line.desc || '',
            amount: round2(parseFloat(p.amount) || 0),
          });
        });
        return;
      }
      mismatches.push({ sid: line.sid, desc: line.desc || '', expected: lineAmount, actual: sum });
    }

    pieces.push({
      key: line.sid,
      sid: line.sid,
      subId: null,
      desc: line.desc || '',
      lineDesc: line.desc || '',
      amount: lineAmount,
    });
  });

  return { pieces, mismatches };
}

/**
 * Divide a person's live side-hours across their hourly attachments,
 * pro-rata by attachment share (total-preserving; falls back to even split
 * when all shares are 0).
 */
function _divideHours(totalHours, attachments) {
  const totalShare = attachments.reduce((s, a) => s + a.share, 0);
  let assigned = 0;
  return attachments.map((a, i) => {
    if (i === attachments.length - 1) return round2(totalHours - assigned);
    const frac = totalShare > 0 ? a.share / totalShare : 1 / attachments.length;
    const h = round2(totalHours * frac);
    assigned = round2(assigned + h);
    return h;
  });
}

/**
 * Core items-mode allocation. Pure — used by the calculator UI (with the
 * entries loaded client-side) AND by payoutEngine's ledger recompute (with
 * live entries server-side), so snapshot and payroll agree by construction.
 *
 * @param {object} params
 * @param {Array}  params.pieces — from buildItemPieces
 * @param {number} params.revenue — job revenue (post-discount)
 * @param {{company,orig,prod,post}} params.splits — job-level percentages
 * @param {object} params.assignments — { [pieceKey]: { shootLead, editLead, soloLead,
 *                 shootHourly:[{rosterId,name,crewId,hourlyRate,estHours}], editHourly:[...] } }
 * @param {Array}  params.timeEntries — this job's time entries (person, hours, category)
 * @param {boolean} [params.estFallback=true] — use estHours when no actual hours are
 *                 logged (calculator display). The payout ledger passes false: estimates
 *                 are never payable and never dock a lead in the ledger.
 * @returns {{ itemAllocs, amounts, origPool, companyBase, unassignedToCompany,
 *             totalOverrun, scale }}
 */
export function computeItemsAllocation({ pieces, revenue, splits, assignments, timeEntries, estFallback = true }) {
  const rev = parseFloat(revenue) || 0;
  const prodPct = parseFloat(splits?.prod) || 0;
  const postPct = parseFloat(splits?.post) || 0;
  const origPct = parseFloat(splits?.orig) || 0;
  const budgetPct = prodPct + postPct;

  // ── Scale piece amounts to revenue (discounts / rounding / addon smear) ──
  const positive = (pieces || []).filter(p => p.amount > 0);
  const rawSum = round2(positive.reduce((s, p) => s + p.amount, 0));
  const scale = rawSum > 0 ? rev / rawSum : 0;

  let effs = positive.map(p => round2(p.amount * scale));
  const drift = round2(rev - effs.reduce((s, e) => s + e, 0));
  if (Math.abs(drift) >= 0.01 && effs.length > 0) {
    // Correct penny drift on the largest piece, clamped at 0
    const bigIdx = effs.reduce((bi, e, i) => (e > effs[bi] ? i : bi), 0);
    effs[bigIdx] = Math.max(0, round2(effs[bigIdx] + drift));
  }

  const origPool = round2(rev * origPct / 100);

  // ── First pass: budgets, shares, per-piece redistribution ────────────────
  const allocs = [];
  let effIdx = 0;
  (pieces || []).forEach(piece => {
    const effAmount = piece.amount > 0 ? effs[effIdx++] : 0;
    const budget = round2(effAmount * budgetPct / 100);
    const shootBase = budgetPct > 0 ? round2(budget * prodPct / budgetPct) : 0;
    const editBase = round2(budget - shootBase);

    const a = assignments?.[piece.key] || {};
    const shootLead = a.shootLead || null;
    const editLead = a.editLead || null;
    const shootHourly = a.shootHourly || [];
    const editHourly = a.editHourly || [];

    // Per-piece redistribution BEFORE hourly netting (mirrors redistributeEmpty):
    // an unled, crew-less side folds its share into the other side when the
    // other side has any crew. Neither side crewed → whole budget unassigned.
    let shootShare = shootBase;
    let editShare = editBase;
    const shootCrewed = !!shootLead || shootHourly.length > 0;
    const editCrewed = !!editLead || editHourly.length > 0;
    if (!shootCrewed && editCrewed) { editShare = round2(editShare + shootShare); shootShare = 0; }
    else if (!editCrewed && shootCrewed) { shootShare = round2(shootShare + editShare); editShare = 0; }

    allocs.push({
      key: piece.key, sid: piece.sid, subId: piece.subId,
      desc: piece.desc, lineDesc: piece.lineDesc,
      amount: piece.amount, effAmount, budget,
      soloLead: !!a.soloLead,
      shoot: { share: shootShare, lead: shootLead ? { ...shootLead } : null, hourlyDefs: shootHourly },
      edit: { share: editShare, lead: editLead ? { ...editLead } : null, hourlyDefs: editHourly },
    });
  });

  // ── Second pass: divide each person's live side-hours across attachments ─
  const sideCat = { shoot: 'shoot', edit: 'edit' };
  const catOf = (c) => {
    const lc = (c || '').toLowerCase();
    if (lc === 'shoot') return 'shoot';
    if (lc === 'edit') return 'edit';
    return null;
  };
  const liveHours = { shoot: {}, edit: {} }; // side → nameLc → hours
  (timeEntries || []).forEach(e => {
    const side = catOf(e.category);
    if (!side) return;
    const lc = (e.person || '').toLowerCase();
    if (!lc) return;
    liveHours[side][lc] = (liveHours[side][lc] || 0) + (parseFloat(e.hours) || 0);
  });

  // Who leads each side anywhere in the job — their own hours are lead work.
  const leadNames = { shoot: new Set(), edit: new Set() };
  allocs.forEach(alloc => {
    ['shoot', 'edit'].forEach(side => {
      const lc = (alloc[side].lead?.name || '').toLowerCase();
      if (lc) leadNames[side].add(lc);
    });
  });

  ['shoot', 'edit'].forEach(side => {
    // Gather attachments per person on this side (across all pieces)
    const byPerson = {}; // nameLc → [{alloc, def, share}]
    allocs.forEach(alloc => {
      (alloc[side].hourlyDefs || []).forEach(def => {
        const lc = (def.name || '').toLowerCase();
        if (!lc) return;
        if (!byPerson[lc]) byPerson[lc] = [];
        byPerson[lc].push({ alloc, def, share: alloc[side].share });
      });
    });

    Object.entries(byPerson).forEach(([lc, attachments]) => {
      let actual = liveHours[sideCat[side]][lc] || 0;
      // A lead's own logged hours are lead work, not hourly work (legacy rule:
      // the lead's hours are advisory and never earn on top of the remainder).
      // Time entries carry no item, so a person who leads some pieces and is
      // hourly on others would otherwise funnel ALL their side-hours into the
      // hourly attachments and get paid twice. Cap those attachments at what
      // they budgeted; the excess is lead work and earns no hourly pay.
      // Pure hourly crew (never a lead on this side) stay uncapped — overrun is
      // still paid in full and absorbed by Company.
      let excludedLeadHours = 0;
      if (leadNames[side].has(lc)) {
        const budgeted = round2(attachments.reduce((s, a) => s + (parseFloat(a.def.estHours) || 0), 0));
        if (actual > budgeted) {
          excludedLeadHours = round2(actual - budgeted);
          actual = budgeted;
        }
      }
      const divided = actual > 0 ? _divideHours(actual, attachments) : null;
      attachments.forEach((att, i) => {
        const rate = parseFloat(att.def.hourlyRate) || 0;
        const estHours = parseFloat(att.def.estHours) || 0;
        const hours = divided ? divided[i] : (estFallback ? estHours : 0);
        const isEstimated = !divided && estFallback && estHours > 0;
        if (!att.alloc[side].hourly) att.alloc[side].hourly = [];
        att.alloc[side].hourly.push({
          rosterId: att.def.rosterId || null,
          name: att.def.name || '',
          crewId: att.def.crewId || null,
          hourlyRate: rate,
          estHours,
          hours,
          isEstimated,
          ...(excludedLeadHours > 0 ? { leadHoursExcluded: excludedLeadHours } : {}),
          earned: round2(hours * rate),
        });
      });
    });
  });

  // ── Third pass: net each side — lead remainder, overrun, company flows ───
  let totalBudgets = 0;
  let totalOverrun = 0;
  let unassignedToCompany = 0;
  let sumShootShares = 0;
  let sumEditShares = 0;

  allocs.forEach(alloc => {
    totalBudgets = round2(totalBudgets + alloc.budget);
    ['shoot', 'edit'].forEach(side => {
      const s = alloc[side];
      s.hourly = s.hourly || [];
      delete s.hourlyDefs;
      s.hourlyTotal = round2(s.hourly.reduce((sum, m) => sum + m.earned, 0));
      s.overrun = Math.max(0, round2(s.hourlyTotal - s.share));
      s.leadEarned = Math.max(0, round2(s.share - s.hourlyTotal));
      if (s.lead) {
        s.lead.earned = s.leadEarned;
      } else {
        // No lead on this side: remainder (after hourly) goes to Company
        unassignedToCompany = round2(unassignedToCompany + s.leadEarned);
        s.leadEarned = 0;
      }
      totalOverrun = round2(totalOverrun + s.overrun);
    });
    sumShootShares = round2(sumShootShares + alloc.shoot.share);
    sumEditShares = round2(sumEditShares + alloc.edit.share);
  });

  // Company base is the residual → exact conservation
  const companyBase = round2(rev - origPool - totalBudgets);

  return {
    itemAllocs: allocs,
    amounts: { company: companyBase, orig: origPool, prod: sumShootShares, post: sumEditShares },
    origPool,
    companyBase,
    unassignedToCompany,
    totalOverrun,
    scale: round2(scale * 10000) / 10000,
  };
}

/**
 * Apply per-department expense deductions to an items allocation.
 * prod expenses reduce shoot leads pro-rata by leadEarned; post expenses
 * reduce edit leads likewise. Hourly and Company are never touched here.
 * Display-only — the payout ledger stays pre-expense (same as legacy).
 */
export function applyItemsExpenseDeductions(itemsResult, expensesByDept) {
  const byDept = { prod: expensesByDept?.prod || 0, post: expensesByDept?.post || 0 };
  const absorbed = { prod: 0, post: 0 };
  const sideOf = { prod: 'shoot', post: 'edit' };

  Object.entries(byDept).forEach(([dept, expense]) => {
    if (expense <= 0) return;
    const side = sideOf[dept];
    const leads = [];
    itemsResult.itemAllocs.forEach(alloc => {
      const s = alloc[side];
      if (s.lead && s.leadEarned > 0) leads.push(s);
    });
    const totalEarned = round2(leads.reduce((sum, s) => sum + s.leadEarned, 0));
    if (totalEarned <= 0) return; // nothing to absorb → stays unabsorbed

    let deducted = 0;
    leads.forEach((s, i) => {
      let share;
      if (i === leads.length - 1) {
        share = round2(Math.min(expense, totalEarned) - deducted);
      } else {
        share = round2(Math.min(expense, totalEarned) * s.leadEarned / totalEarned);
      }
      const d = Math.min(share, s.leadEarned);
      deducted = round2(deducted + d);
      s.leadEarned = round2(s.leadEarned - d);
      s.lead.earned = s.leadEarned;
      s.lead.expenseDeduction = round2((s.lead.expenseDeduction || 0) + d);
      s.expenseDeduction = round2((s.expenseDeduction || 0) + d);
    });
    absorbed[dept] = deducted;
  });

  return absorbed;
}

/**
 * Aggregate items allocations into legacy-shaped prod/post mirrors so the
 * many snapshot consumers (calendar, gcal, payroll views, rate lookup)
 * keep working. `lead` holds the single lead when there is exactly one
 * distinct lead on the side; `leads` is always authoritative.
 */
export function buildItemsMirror(itemAllocs) {
  const mirror = {};
  const sides = { prod: 'shoot', post: 'edit' };

  Object.entries(sides).forEach(([dept, side]) => {
    const leadMap = {}; // nameLc → {rosterId,name,crewId,earned,expenseDeduction}
    const hourlyMap = {}; // nameLc → aggregated member
    let budget = 0, hourlyTotal = 0, overrun = 0, leadEarned = 0;

    (itemAllocs || []).forEach(alloc => {
      const s = alloc[side];
      if (!s) return;
      budget = round2(budget + (s.share || 0));
      hourlyTotal = round2(hourlyTotal + (s.hourlyTotal || 0));
      overrun = round2(overrun + (s.overrun || 0));
      if (s.lead) {
        leadEarned = round2(leadEarned + (s.leadEarned || 0));
        const lc = (s.lead.name || '').toLowerCase();
        if (!leadMap[lc]) {
          leadMap[lc] = {
            rosterId: s.lead.rosterId || null, name: s.lead.name || '',
            crewId: s.lead.crewId || null, earned: 0, expenseDeduction: 0,
          };
        }
        const cur = leadMap[lc];
        cur.earned = round2(cur.earned + (s.leadEarned || 0));
        cur.expenseDeduction = round2(cur.expenseDeduction + (s.lead.expenseDeduction || 0));
      }
      (s.hourly || []).forEach(m => {
        const lc = (m.name || '').toLowerCase();
        if (!hourlyMap[lc]) {
          hourlyMap[lc] = {
            rosterId: m.rosterId || null, name: m.name || '', crewId: m.crewId || null,
            hourlyRate: m.hourlyRate || 0, hours: 0, estHours: 0, isEstimated: false, earned: 0,
          };
        }
        const cur = hourlyMap[lc];
        cur.hours = round2(cur.hours + (m.hours || 0));
        cur.estHours = round2(cur.estHours + (m.estHours || 0));
        cur.isEstimated = cur.isEstimated || !!m.isEstimated;
        cur.earned = round2(cur.earned + (m.earned || 0));
      });
    });

    const leads = Object.values(leadMap);
    leads.forEach(l => { if (!l.expenseDeduction) delete l.expenseDeduction; });
    mirror[dept] = {
      lead: leads.length === 1 ? leads[0] : null,
      leads,
      hourly: Object.values(hourlyMap),
      budget,
      hourlyTotal,
      overrun,
      leadEarned,
    };
  });

  return mirror;
}

/**
 * Assemble the items-mode snapshot. Same merge contract as buildSnapshot
 * (spreads existingSnapshot first to preserve quote keys), plus:
 *   payMode:'items', itemAllocs (durable inputs + display values),
 *   itemSplits, and prod/post written as aggregate mirrors.
 */
export function buildItemsSnapshot({
  revenue, autoScale, splits, origResult, itemsResult, itemSplits,
  expenseMeta, existingSnapshot,
}) {
  const mirror = buildItemsMirror(itemsResult.itemAllocs);
  const companyTotal = round2(
    itemsResult.companyBase
    + (origResult?.remainder || 0)
    + itemsResult.unassignedToCompany
    - itemsResult.totalOverrun
  );

  const snapshot = {
    ...(existingSnapshot || {}),

    payMode: 'items',
    autoScale: !!autoScale,
    splits: { ...splits },
    amounts: { ...itemsResult.amounts, company: companyTotal },
    totalRevenue: revenue,
    redistribution: { prodToPost: 0, postToProd: 0, toCompany: itemsResult.unassignedToCompany },

    originators: origResult?.originators || [],
    originatorRemainder: origResult?.remainder || 0,

    itemAllocs: itemsResult.itemAllocs,
    itemSplits: itemSplits || {},

    prod: mirror.prod,
    post: mirror.post,

    savedAt: new Date().toISOString(),
    expenseMeta: expenseMeta || null,
  };

  // An items-mode save supersedes any staycation plan — its derived dollars
  // must not linger and contradict the fresh amounts
  delete snapshot.staycation;

  return snapshot;
}

// ═══════════════════════════════════════════════════════════════════════
// STAYCATION MODE (payMode: 'staycation') — segment-based hourly plan
//
// Model (Shay's Branson plan): every crew member is hourly; pay =
// (segments × hrsPerSegment + extraHours) × rate. Company keeps the
// remainder (revenue − labor); the company $ / % input is a TARGET the
// variance is measured against, not a guaranteed cut.
//
// The snapshot is written in canonical legacy shape — crew mirrored as
// post.hourly (segment editing is edit work) — so the ledger, LogWork,
// payroll views and assigned-crew extraction all reuse the legacy path
// unchanged. The `staycation` block persists the durable inputs for
// calculator restore.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Compute the staycation split.
 *
 * @param {number} revenue — full project revenue
 * @param {number} companyTarget — desired company profit in $ (target, not a cut)
 * @param {number} hrsPerSegment — budgeted editing hours per segment
 * @param {Array<{rosterId, name, crewId, rate, segments, extraHours}>} rows
 * @returns {{ crew, totalSegments, totalHours, laborTotal, companyTarget,
 *             targetPct, availableForLabor, netProfit, variance }}
 */
export function computeStaycationSplit({ revenue, companyTarget, hrsPerSegment, rows }) {
  const rev = parseFloat(revenue) || 0;
  const target = round2(Math.max(0, parseFloat(companyTarget) || 0));
  const hps = Math.max(0, parseFloat(hrsPerSegment) || 0);

  const crew = (rows || []).map(r => {
    const rate = parseFloat(r.rate) || 0;
    const segments = Math.max(0, Math.round(parseFloat(r.segments) || 0));
    const extraHours = Math.max(0, parseFloat(r.extraHours) || 0);
    const segmentHours = round2(segments * hps);
    const hours = round2(segmentHours + extraHours);
    return { ...r, rate, segments, extraHours, segmentHours, hours, earned: round2(hours * rate) };
  });

  const laborTotal = round2(crew.reduce((s, m) => s + m.earned, 0));
  const netProfit = round2(rev - laborTotal);

  return {
    crew,
    totalSegments: crew.reduce((s, m) => s + m.segments, 0),
    totalHours: round2(crew.reduce((s, m) => s + m.hours, 0)),
    laborTotal,
    companyTarget: target,
    targetPct: rev > 0 ? round2((target / rev) * 100) : 0,
    availableForLabor: round2(rev - target),
    netProfit,
    variance: round2(netProfit - target),
  };
}

/**
 * Assemble the staycation snapshot. Same merge contract as buildSnapshot
 * (spreads existingSnapshot first to preserve quote keys). Crew are written
 * as post.hourly with estHours = planned hours, so downstream consumers
 * (ledger, rate lookup, payroll, assigned-crew) treat them as legacy
 * post-production hourly members.
 */
export function buildStaycationSnapshot({ revenue, result, hrsPerSegment, existingSnapshot }) {
  const rev = parseFloat(revenue) || 0;
  const laborPct = rev > 0 ? round2((result.laborTotal / rev) * 100) : 0;

  const snapshot = {
    ...(existingSnapshot || {}),

    payMode: 'staycation',
    autoScale: false,
    // splits/amounts reflect ACTUALS (company = remainder), not the target
    splits: { company: round2(100 - laborPct), orig: 0, prod: 0, post: laborPct },
    amounts: { company: result.netProfit, orig: 0, prod: 0, post: result.laborTotal },
    totalRevenue: rev,
    redistribution: { prodToPost: 0, postToProd: 0, toCompany: 0 },

    originators: [],
    originatorRemainder: 0,

    prod: { lead: null, hourly: [], budget: 0, hourlyTotal: 0, overrun: 0, leadEarned: 0 },
    post: {
      lead: null,
      hourly: result.crew.map(m => ({
        rosterId: m.rosterId || null,
        name: m.name || '',
        crewId: m.crewId || null,
        hourlyRate: m.rate,
        hours: 0,
        estHours: m.hours,
        isEstimated: m.hours > 0,
        earned: m.earned,
      })),
      budget: result.laborTotal,
      hourlyTotal: result.laborTotal,
      overrun: 0,
      leadEarned: 0,
    },

    // Durable inputs for calculator restore + plan reference
    staycation: {
      companyTarget: result.companyTarget,
      hrsPerSegment: Math.max(0, parseFloat(hrsPerSegment) || 0),
      rows: result.crew.map(m => ({
        rosterId: m.rosterId || null,
        name: m.name || '',
        crewId: m.crewId || null,
        rate: m.rate,
        segments: m.segments,
        extraHours: m.extraHours,
      })),
      laborTotal: result.laborTotal,
      netProfit: result.netProfit,
      variance: result.variance,
    },

    savedAt: new Date().toISOString(),
    expenseMeta: null,
  };

  // A staycation save supersedes any per-item allocation (itemSplits stay:
  // line-keyed, reusable if the job returns to items mode)
  delete snapshot.itemAllocs;

  return snapshot;
}

/**
 * All leads of a dept section (snapshot prod/post): items-mode mirrors carry
 * leads[] (one entry per distinct lead); legacy snapshots have a single lead.
 */
export function deptLeads(d) {
  if (Array.isArray(d?.leads) && d.leads.length) return d.leads;
  return d?.lead ? [d.lead] : [];
}

/**
 * All people referenced by a snapshot (any format), regardless of earned.
 * For crew lists, calendars, notifications — NOT for dollars.
 * @returns {Array<{name, rosterId, crewId, roles: string[]}>}
 */
export function getAllPeople(raw) {
  const snap = normalizeSnapshot(raw);
  const map = {};
  const add = (p, role) => {
    if (!p?.name) return;
    const lc = p.name.toLowerCase();
    if (!map[lc]) map[lc] = { name: p.name, rosterId: p.rosterId || null, crewId: p.crewId || null, roles: [] };
    const cur = map[lc];
    if (!cur.rosterId && p.rosterId) cur.rosterId = p.rosterId;
    if (!cur.crewId && p.crewId) cur.crewId = p.crewId;
    if (!cur.roles.includes(role)) cur.roles.push(role);
  };

  (snap.originators || []).forEach(o => { add(o, 'orig'); });
  ['prod', 'post'].forEach(dept => {
    const d = snap[dept];
    if (!d) return;
    const leads = d.leads?.length ? d.leads : (d.lead ? [d.lead] : []);
    leads.forEach(l => { add(l, `${dept}_lead`); });
    (d.hourly || []).forEach(h => { add(h, `${dept}_hourly`); });
  });

  return Object.values(map);
}

// ─── Normalize: old format → canonical ──────────────────────────────────

/**
 * Normalize ANY calcSnapshot format into the canonical shape.
 *
 * Handles:
 *   - V1 flat keys: rfPct, origPct, rfAmt, origAmt, teams.orig[].amt/.hourlyAmt
 *   - Current V2: splits.rf, amounts.rf, teams.orig[].earned
 *   - New budget-owner: originators[], prod.lead, prod.hourly[]
 *   - Quote-only snapshots (deliverableLines, no teams)
 *
 * @param {object|null} raw — raw snapshot from job.calcSnapshot
 * @returns {object} canonical snapshot shape (safe to read without null checks)
 */
export function normalizeSnapshot(raw) {
  if (!raw) {
    return _emptySnapshot();
  }

  // Items mode (payMode:'items') — passthrough; rebuild prod/post mirrors
  // from itemAllocs if a writer omitted them
  if (raw.payMode === 'items') {
    const base = { ..._emptySnapshot(), ...raw };
    if (!raw.prod || !raw.post) {
      const mirror = buildItemsMirror(raw.itemAllocs || []);
      base.prod = raw.prod || mirror.prod;
      base.post = raw.post || mirror.post;
    }
    return base;
  }

  // Already in new format — passthrough
  if (raw.originators !== undefined || raw.prod?.lead !== undefined) {
    return {
      ..._emptySnapshot(),
      ...raw,
    };
  }

  // ── V1 / current V2 format ────────────────────────────────────────────
  const splits = raw.splits
    ? {
        company: raw.splits.company ?? raw.splits.rf ?? 0,
        orig:    raw.splits.orig ?? 0,
        prod:    raw.splits.prod ?? 0,
        post:    raw.splits.post ?? 0,
      }
    : {
        company: (raw.rfPct ?? 0) * 100,
        orig:    (raw.origPct ?? 0) * 100,
        prod:    (raw.prodPct ?? 0) * 100,
        post:    (raw.postPct ?? 0) * 100,
      };

  const amounts = raw.amounts
    ? {
        company: raw.amounts.company ?? raw.amounts.rf ?? 0,
        orig:    raw.amounts.orig ?? 0,
        prod:    raw.amounts.prod ?? 0,
        post:    raw.amounts.post ?? 0,
      }
    : {
        company: raw.rfAmt ?? 0,
        orig:    raw.origAmt ?? 0,
        prod:    raw.prodAmt ?? 0,
        post:    raw.postAmt ?? 0,
      };

  // Convert old teams format → new structure
  const teams = raw.teams || {};
  const origMembers = teams.orig || [];
  const prodMembers = teams.prod || [];
  const postMembers = teams.post || [];

  // Originators: old format had type:'lead' with pct
  const originators = origMembers.map(m => ({
    rosterId: m.rosterId || null,
    name:     m.name || '',
    crewId:   m.crewId || null,
    pct:      m.pct || 0,
    earned:   m.earned ?? m.amt ?? 0,
  }));

  // Prod/Post: separate lead (first member with type !== 'hourly') from hourly
  function convertDept(members, budget) {
    const lead = members.find(m => m.type !== 'hourly') || null;
    const hourly = members.filter(m => m.type === 'hourly').map(m => ({
      rosterId:   m.rosterId || null,
      name:       m.name || '',
      crewId:     m.crewId || null,
      hourlyRate: m.hourlyRate || m.rate || 0,
      hours:      m.hours || 0,
      estHours:   m.estHours || 0,
      isEstimated: m.isEstimated || false,
      earned:     m.earned ?? m.hourlyAmt ?? 0,
    }));

    const hourlyTotal = hourly.reduce((s, m) => s + m.earned, 0);
    const leadEarned = lead ? (lead.earned ?? lead.amt ?? Math.max(0, budget - hourlyTotal)) : 0;

    return {
      lead: lead ? {
        rosterId: lead.rosterId || null,
        name:     lead.name || '',
        crewId:   lead.crewId || null,
        earned:   leadEarned,
      } : null,
      hourly,
      budget,
      hourlyTotal: round2(hourlyTotal),
      overrun: Math.max(0, round2(hourlyTotal - budget)),
      leadEarned: round2(leadEarned),
    };
  }

  return {
    // Preserve quote keys
    deliverableLines: raw.deliverableLines,
    quoteNumber:      raw.quoteNumber,
    mode:             raw.mode,
    sqft:             raw.sqft,

    autoScale:     raw.autoScale ?? raw.manualOverride === false ?? false,
    splits,
    amounts,
    totalRevenue:  raw.totalRevenue ?? raw.revenue ?? 0,
    redistribution: raw.redistribution || { prodToPost: 0, postToProd: 0, toCompany: 0 },

    originators,
    originatorRemainder: raw.originatorRemainder ?? 0,

    prod: convertDept(prodMembers, amounts.prod),
    post: convertDept(postMembers, amounts.post),

    savedAt: raw.savedAt || null,
    expenseMeta: raw.expenseMeta || null,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────

function _emptySnapshot() {
  return {
    autoScale: false,
    splits:    { company: 0, orig: 0, prod: 0, post: 0 },
    amounts:   { company: 0, orig: 0, prod: 0, post: 0 },
    totalRevenue: 0,
    redistribution: { prodToPost: 0, postToProd: 0, toCompany: 0 },
    originators: [],
    originatorRemainder: 0,
    prod: { lead: null, hourly: [], budget: 0, hourlyTotal: 0, overrun: 0, leadEarned: 0 },
    post: { lead: null, hourly: [], budget: 0, hourlyTotal: 0, overrun: 0, leadEarned: 0 },
    expenseMeta: null,
    savedAt: null,
  };
}

/**
 * Get all people with earned amounts from a normalized snapshot.
 * Useful for downstream consumers that just need a flat list.
 *
 * @param {object} snap — normalized snapshot
 * @returns {Array<{name, earned, role, type}>}
 */
export function getAllEarnings(snap) {
  const result = [];

  (snap.originators || []).forEach(o => {
    if (o.earned > 0) result.push({ name: o.name, earned: o.earned, role: 'orig', type: 'originator' });
  });

  ['prod', 'post'].forEach(dept => {
    const d = snap[dept];
    if (!d) return;
    // Items-mode mirrors carry leads[] (one entry per distinct lead);
    // legacy snapshots have the single lead
    const leads = d.leads?.length ? d.leads : (d.lead ? [d.lead] : []);
    leads.forEach(l => {
      if (l.earned > 0) result.push({ name: l.name, earned: l.earned, role: dept, type: 'lead' });
    });
    (d.hourly || []).forEach(h => {
      if (h.earned > 0) result.push({ name: h.name, earned: h.earned, role: dept, type: 'hourly' });
    });
  });

  return result.sort((a, b) => b.earned - a.earned);
}
